import { Fragment, forwardRef, startTransition, useId, useRef } from "react"
import type { CSSProperties, FC, ReactElement, RefObject, UIEvent } from "react"

import isArray from "lodash-es/isArray"
import isFunction from "lodash-es/isFunction"
import isUndefined from "lodash-es/isUndefined"
import join from "lodash-es/join"
import map from "lodash-es/map"
import { useBoolean, useMount, useNumber, useUpdateEffect } from "react-use"
import type { CounterActions } from "react-use/lib/useCounter"

import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { buildCssClassName, idleCb, withPressHandler } from "@/3514/utils"
import { seconds } from "@/3514/utils/time.utils"
import { type TEmptyCallback } from "@/shared/types/functions"

import config from "./accordion.config"
import classes from "./accordion.styles"
import type { TAccordionProps, TAccordionRenderFunction } from "./accordion.types"
import utils from "./accordion.utils"

const Accordion: FC<TAccordionProps> = forwardRef<HTMLDivElement, TAccordionProps>(
    (
        {
            containerClassName = undefined,
            renderBody,
            renderHeader,
            renderIcons = undefined,
            onExpand = undefined,
            colorVariant,
            bordersVariant,
            withHoverColors: withHover = false,
            isExpandable = false,
            isDefaultExpanded = false,
            headerBorderRadius: headerRadius = 10
        }: TAccordionProps,
        ref: RefObject<HTMLDivElement>
    ): ReactElement => {
        const accId: string = useId()

        const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

        const [isExpanded, setIsExpanded]: ReturnType<typeof useBoolean> = useBoolean(false)

        const [bodyHeight, { set: setBodyHeight }]: [number, CounterActions] = useNumber(0)

        const bodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
        const bodyContentRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

        useMount((): void => {
            const dispatcher: TEmptyCallback = (): void =>
                startTransition((): void => isDefaultExpanded && setIsExpanded(true))

            if (breakpoints.xs || breakpoints.sm) requestAnimationFrame((): void => dispatcher())
            else idleCb((): void => dispatcher(), seconds(20))
        })

        useUpdateEffect((): void => (isExpanded && void onExpand?.(), void 0), [isExpanded, onExpand])

        useUpdateEffect((): void => {
            if (!isExpandable || !bodyRef.current) return

            startTransition((): void => setBodyHeight(isExpanded ? bodyContentRef.current?.scrollHeight : 0))
        }, [isExpandable, isExpanded])

        const toggleExpansion: TEmptyCallback = (): void =>
            startTransition((): void => {
                if (isExpandable) setIsExpanded((prev: boolean): boolean => !prev)
            })

        const headerAction: ReturnType<typeof withPressHandler> = withPressHandler((e: UIEvent): void => {
            e.preventDefault()
            toggleExpansion()
        })

        const borderRadiusStyles: CSSProperties = utils.getHeaderBorderRadiusStyles({
            config: headerRadius,
            isExpanded
        })

        return (
            <div
                className={buildCssClassName(classes.container(), { [containerClassName]: containerClassName })}
                ref={ref}
            >
                <div
                    className={buildCssClassName(classes.headerWrapper({ isExpandable }), {
                        [config.headerColorsMap[colorVariant]({ isExpanded, isExpandable, withHover })]: colorVariant,
                        [config.headerBordersMap[bordersVariant]]: bordersVariant
                    })}
                    style={borderRadiusStyles}
                    data-accordion-header="true"
                >
                    <button
                        type="button"
                        className={classes.headerTrigger({ isExpandable })}
                        aria-expanded={isExpandable ? isExpanded : undefined}
                        aria-label={
                            isExpandable ? join([isExpanded ? "Collapse" : "Expand", "accordion"], " ") : undefined
                        }
                        disabled={!isExpandable}
                        data-accordion-trigger="true"
                        {...(isExpandable && {
                            onClick: headerAction,
                            onKeyDown: headerAction
                        })}
                    >
                        {isFunction(renderHeader)
                            ? renderHeader(isExpanded, { toggle: toggleExpansion })
                            : renderHeader}
                    </button>

                    {isUndefined(renderIcons)
                        ? null
                        : isArray(renderIcons) && (
                              <div className={classes.iconsContainer()}>
                                  {map(
                                      renderIcons,
                                      (i: TAccordionRenderFunction, idx: number): ReactElement =>
                                          isFunction(i) ? (
                                              <Fragment key={`${accId}-${idx}`}>
                                                  {i(isExpanded, { toggle: toggleExpansion })}
                                              </Fragment>
                                          ) : (
                                              <Fragment key={`${accId}-${idx}`}>{i}</Fragment>
                                          )
                                  )}
                              </div>
                          )}
                </div>

                <div
                    className={classes.body()}
                    style={{ maxHeight: `${bodyHeight}px` }}
                    aria-hidden={!isExpanded}
                    ref={bodyRef}
                >
                    <div className={classes.bodyContent()} ref={bodyContentRef}>
                        {isFunction(renderBody) ? renderBody(isExpanded) : renderBody}
                    </div>
                </div>
            </div>
        )
    }
)

Accordion.displayName = "Accordion"

export { Accordion }
